import { useState } from "react";
import { useTranslation } from "react-i18next";
import CoachMatchCancelFlowModal, { OTHER_VALUE } from "@components/Subscriptions/CoachMatchCancelFlowModal";
import type { CancelFlowStep } from "@components/Subscriptions/CoachMatchCancelFlowModal";
import cancelStripeSubscription from "@api/cancelStripeSubscription";
import cancelZuoraAthletePremiumSubscription from "@api/cancelZuoraAthletePremiumSubscription";
import { useSubscriptionContext } from "@context/SubscriptionContext";
import { useNotificationContext } from "@context/NotificationContext";
import { Subscription } from "@appTypes/index";
import { SUBSCRIPTION_SOURCES } from "@constants/subscriptions";
import { trackEvent } from "@utils/userEvents";

const CANCEL_ERROR_MESSAGE = "Error cancelling subscription. Please try again or contact Customer Support for help";
const CANCEL_SUCCESS_MESSAGE = "Your subscription has been cancelled successfully";
const FEATURE_ENGAGED_EVENT = "Feature Engaged";
const COACH_MATCH_CANCEL_FEATURE = "CoachMatchCancel";
const COACH_MATCH_CANCEL_LOCATION = "CoachMatchCancelPage";

const useHandleCoachMatchCancelFlow = (subscription: Subscription) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [step, setStep] = useState<CancelFlowStep>("survey");
  const [selectedReason, setSelectedReason] = useState("");
  const [additionalText, setAdditionalText] = useState("");
  const [isCancelling, setIsCancelling] = useState(false);
  const { updateSubscriptionLocally } = useSubscriptionContext();
  const { showSuccess, showError } = useNotificationContext();
  const { t } = useTranslation();
  const isStripe = subscription.source === SUBSCRIPTION_SOURCES.STRIPE;

  const isOther = selectedReason === OTHER_VALUE;
  const isContinueDisabled = !selectedReason;

  const resetState = () => {
    setStep("survey");
    setSelectedReason("");
    setAdditionalText("");
  };

  const handleCoachMatchCancelModalOpen = () => {
    resetState();
    setIsModalOpen(true);
  };

  const handleContinue = () => {
    const text = isOther ? additionalText : undefined;
    void trackEvent(FEATURE_ENGAGED_EVENT, {
      feature: COACH_MATCH_CANCEL_FEATURE,
      action: "Continue",
      location: COACH_MATCH_CANCEL_LOCATION,
      details: [{ cancel_reason: selectedReason, additional_text: text ?? "" }],
    });
    setStep("confirm");
  };

  const handleKeepSubscription = () => {
    void trackEvent(FEATURE_ENGAGED_EVENT, {
      feature: COACH_MATCH_CANCEL_FEATURE,
      action: "KeepSubscription",
      location: COACH_MATCH_CANCEL_LOCATION,
    });
    setIsModalOpen(false);
    resetState();
  };

  const handleClose = () => {
    setIsModalOpen(false);
    resetState();
  };

  const handleConfirmCancel = async () => {
    setIsCancelling(true);

    if (isStripe) {
      if (!subscription.subscriptionIdentifier) {
        setIsCancelling(false);
        showError(t(CANCEL_ERROR_MESSAGE));
        return;
      }
      const response = await cancelStripeSubscription(subscription.subscriptionIdentifier);
      setIsCancelling(false);
      if (!response.success) {
        showError(t(CANCEL_ERROR_MESSAGE));
        return;
      }
      setIsModalOpen(false);
      updateSubscriptionLocally(subscription.subscriptionIdentifier, { autoRenew: false });
      showSuccess(response.data?.message ?? t(CANCEL_SUCCESS_MESSAGE));
      void trackEvent(FEATURE_ENGAGED_EVENT, {
        feature: COACH_MATCH_CANCEL_FEATURE,
        action: "ConfirmCancel",
        location: COACH_MATCH_CANCEL_LOCATION,
        details: [{
          cancel_reason: selectedReason,
          additional_text: (isOther ? additionalText : undefined) ?? "",
        }],
      });
      resetState();
      return;
    }

    const response = await cancelZuoraAthletePremiumSubscription();
    setIsCancelling(false);
    if (!response.success) {
      showError(t(CANCEL_ERROR_MESSAGE));
      return;
    }
    setIsModalOpen(false);
    if (subscription.subscriptionIdentifier) {
      updateSubscriptionLocally(subscription.subscriptionIdentifier, { autoRenew: false });
    }
    showSuccess(t(CANCEL_SUCCESS_MESSAGE));
    void trackEvent(FEATURE_ENGAGED_EVENT, {
      feature: COACH_MATCH_CANCEL_FEATURE,
      action: "ConfirmCancel",
      location: COACH_MATCH_CANCEL_LOCATION,
      details: [{
        cancel_reason: selectedReason,
        additional_text: (isOther ? additionalText : undefined) ?? "",
      }],
    });
    resetState();
  };

  const modal = (
    <CoachMatchCancelFlowModal
      isOpen={isModalOpen}
      step={step}
      selectedReason={selectedReason}
      additionalText={additionalText}
      isOther={isOther}
      isContinueDisabled={isContinueDisabled}
      isProcessing={isCancelling}
      onReasonChange={setSelectedReason}
      onAdditionalTextChange={setAdditionalText}
      onContinue={handleContinue}
      onKeepSubscription={handleKeepSubscription}
      onConfirm={handleConfirmCancel}
      onClose={handleClose}
    />
  );

  return { handleCoachMatchCancelModalOpen, modal };
};

export default useHandleCoachMatchCancelFlow;
